import { useMemo, useState } from "react";
import { Download, ExternalLink, MapPin } from "lucide-react";
import { CalendarLinks } from "@/components/CalendarLinks";
import { motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/SectionHeading";
import { csvCell, formatDateRange, shortDate, slugify } from "@/utils/format";

const FOCUS_MODALITIES = new Set(["Foso Universal", "Trap 5", "Foso Olímpico", "FO / FU"]);

export function AnnualCalendar({ calendar = [] }) {
  const focusedCalendar = useMemo(() => calendar.filter((item) => FOCUS_MODALITIES.has(item.modality)), [calendar]);
  const months = useMemo(() => ["Todo el año", ...new Set(focusedCalendar.map((item) => item.month))], [focusedCalendar]);
  const [month, setMonth] = useState("Todo el año");
  const filtered = month === "Todo el año" ? focusedCalendar : focusedCalendar.filter((item) => item.month === month);
  const sourceUrl = calendar.find((item) => item.source_url)?.source_url;

  const downloadCsv = () => {
    const rows = ["Fecha,Tirada,Campo,Ubicación,Provincia,Modalidad,Platos,Estado,Descripción,Cartel,Resultados"].concat(
      filtered.map((item) => [item.date, item.title, item.club, item.location, item.province, item.modality, item.targets, item.status, item.description, item.poster_url, item.results_url].map(csvCell).join(",")),
    );
    const url = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "calendario-tiro-al-plato-2026.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section id="calendario" className="section-shell" data-testid="calendar-section">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading number="03" eyebrow="Calendario anual conectado" title="La hoja oficial, convertida en agenda" description="Los eventos se sincronizan desde tu Google Sheet, conservan la descripción y muestran el enlace al cartel cuando la celda lo contiene como URL." />
        <div className="mb-12 flex flex-wrap gap-3">
          {sourceUrl ? (
            <Button asChild variant="outline" className="h-12 rounded-none border-white/15 bg-white/[0.03] px-6 font-mono text-xs uppercase tracking-[0.22em] text-slate-100 hover:border-clay/60 hover:bg-clay/10 hover:text-clay">
              <a href={sourceUrl} target="_blank" rel="noreferrer" data-testid="calendar-source-link">Abrir hoja <ExternalLink className="h-4 w-4" /></a>
            </Button>
          ) : null}
          <Button type="button" onClick={downloadCsv} className="h-12 rounded-none bg-clay px-6 font-mono text-xs uppercase tracking-[0.22em] text-charcoal hover:bg-brass" data-testid="calendar-download-csv">
            Descargar CSV <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mb-10 flex gap-3 overflow-x-auto pb-3" data-testid="calendar-month-filter-group">
        {months.map((item) => (
          <button key={item} type="button" onClick={() => setMonth(item)} className={`filter-chip shrink-0 ${month === item ? "filter-chip-active" : ""}`} data-testid={`calendar-month-${slugify(item)}`}>
            {item}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="calendar-grid">
        {filtered.map((item, index) => (
          <motion.article key={item.id} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(index, 8) * 0.04 }} className="calendar-card" data-testid={`calendar-item-${item.id}`}>
            <div className="calendar-date-block">
              <span className="font-display text-5xl leading-none text-clay">{shortDate(item.date).split(" ")[0]}{item.end_date ? <span className="text-3xl text-slate-400">–{shortDate(item.end_date).split(" ")[0]}</span> : null}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{item.month.slice(0, 3)}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`status-dot ${item.status === "Cancelada" || item.status === "Suspendida" ? "status-dot-muted" : ""}`} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">{item.status}</span>
                {item.targets ? <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">· {item.targets} platos</span> : null}
              </div>
              <h3 className="font-display text-3xl uppercase leading-none text-slate-50">{item.title}</h3>
              <p className="mt-3 flex items-center gap-2 text-sm text-slate-400"><MapPin className="h-4 w-4 shrink-0 text-clay" />{item.club ? `${item.club} · ` : ""}{item.location} · {item.province}</p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-slate-500">{item.modality} · {formatDateRange(item.date, item.end_date)}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {item.poster_url ? (
                  <a href={item.poster_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-clay/35 bg-clay/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay transition-colors hover:bg-clay hover:text-charcoal" data-testid={`calendar-poster-${item.id}`}>
                    Ver cartel <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
                <CalendarLinks item={item} prefix={`calendar-results-${item.id}`} />
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
